import React, { useState, useRef } from "react";
import {
  Button,
  Snackbar,
  Alert,
  Stack,
  Skeleton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { importDistroNationSheet } from "../../utils/importGoogleSheet";
import { importFromCsv, generateCsvTemplate } from "../../utils/importCsv";
import { OutreachCategory } from "../../types/outreach";
import { themeColors } from "../../utils/theme";

interface ImportButtonsProps {
  onImportComplete: () => void;
  activeTab: number;
  vertical?: boolean;
}

const ImportButtons: React.FC<ImportButtonsProps> = ({
  onImportComplete,
  activeTab,
  vertical = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Map activeTab index to OutreachCategory
  const getActiveCategory = (): OutreachCategory => {
    switch (activeTab) {
      case 0:
        return OutreachCategory.REACTION_CHANNELS;
      case 1:
        return OutreachCategory.PLAYLISTING_CHANNELS;
      case 2:
        return OutreachCategory.SPOTIFY_PLAYLISTS;
      case 3:
        return OutreachCategory.MUSIC_BLOGS;
      case 4:
        return OutreachCategory.CAMPAIGNS;
      default:
        return OutreachCategory.REACTION_CHANNELS;
    }
  };

  // Check if the current category is importable
  const isImportable = (): boolean => {
    const category = getActiveCategory();
    return category !== OutreachCategory.CAMPAIGNS;
  };

  const handleGoogleSheetImport = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      await importDistroNationSheet();
      setSuccess(true);
      onImportComplete();
    } catch (err) {
      console.error("Import failed:", err);
      setError("Failed to import data from Google Sheet. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCsvImport = () => {
    // Trigger file input click
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const category = getActiveCategory();

      // Skip import for CAMPAIGNS category
      if (category === OutreachCategory.CAMPAIGNS) {
        throw new Error("Importing campaigns from CSV is not supported");
      }

      await importFromCsv(file, category as any);
      setSuccess(true);
      onImportComplete();
    } catch (err: any) {
      console.error("CSV Import failed:", err);
      setError(err.message || "Failed to import CSV data. Please try again.");
    } finally {
      setLoading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  const handleTemplateDownload = () => {
    const category = getActiveCategory();

    // Skip template download for CAMPAIGNS category
    if (category === OutreachCategory.CAMPAIGNS) {
      setError("Template download for campaigns is not supported");
      return;
    }

    const csvContent = generateCsvTemplate(category as any);

    // Create a Blob with the CSV content
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = url;

    // Set the filename based on the category
    let filename = "";
    switch (category) {
      case OutreachCategory.REACTION_CHANNELS:
        filename = "reaction_channels_template.csv";
        break;
      case OutreachCategory.PLAYLISTING_CHANNELS:
        filename = "playlisting_channels_template.csv";
        break;
      case OutreachCategory.SPOTIFY_PLAYLISTS:
        filename = "spotify_playlists_template.csv";
        break;
      case OutreachCategory.MUSIC_BLOGS:
        filename = "music_blogs_template.csv";
        break;
      default:
        filename = "template.csv";
    }

    link.setAttribute("download", filename);

    // Append the link to the body
    document.body.appendChild(link);

    // Trigger the download
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {isImportable() ? (
        <Stack direction={vertical ? "column" : "row"} spacing={2}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={
              loading ? (
                <Skeleton 
                  variant="circular" 
                  width={20} 
                  height={20}
                  sx={{
                    bgcolor: `${themeColors.background}40`,
                    '&::after': {
                      background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                    },
                  }}
                />
              ) : (
                <CloudUploadIcon />
              )
            }
            onClick={handleGoogleSheetImport}
            disabled={loading}
          >
            {loading ? "Importing..." : "Import Google Sheet"}
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={
              loading ? (
                <Skeleton 
                  variant="circular" 
                  width={20} 
                  height={20}
                  sx={{
                    bgcolor: `${themeColors.background}40`,
                    '&::after': {
                      background: `linear-gradient(90deg, transparent, ${themeColors.background}60, transparent)`,
                    },
                  }}
                />
              ) : (
                <InsertDriveFileIcon />
              )
            }
            onClick={handleCsvImport}
            disabled={loading}
          >
            {loading ? "Importing..." : "Import CSV"}
          </Button>

          {/* Hidden file input for CSV upload */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".csv"
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            color="primary"
            startIcon={<InsertDriveFileIcon />}
            onClick={handleTemplateDownload}
            disabled={loading}
          >
            Template Download
          </Button>
        </Stack>
      ) : null}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{ width: "100%" }}
        >
          Data imported successfully!
        </Alert>
      </Snackbar>
    </>
  );
};

export default ImportButtons;
